// ./src/components/generic/MediaSelector/stockMedia.ts
//
// Stock-media search for the MediaSelector (Unsplash photos, GIPHY gifs, Pexels
// photos/videos). Each provider is reached through a thin pass-through proxy
// worker that injects the API key and returns the provider's EXACT JSON — so we
// just hit `{PROXY}/{provider path}` and normalize the response client-side.
//
// Graceful degrade: a source whose proxy base is empty is reported unavailable
// (so its tab is hidden) rather than erroring.

import { fetchWithLimit } from "@em/api";

import { orientationParam, type StockShape } from "./stockShape";

export type StockSource = "unsplash" | "giphy" | "pexels";

// Proxy bases (override per-env via VITE_*). Keys live inside the workers.
const PROXY: Record<StockSource, string> = {
  unsplash: import.meta.env.VITE_UNSPLASH_PROXY || "https://partylabzunsplash.9246401ci.workers.dev",
  giphy: import.meta.env.VITE_GIPHY_PROXY || "https://partylabzgiphy.9246401ci.workers.dev",
  pexels: import.meta.env.VITE_PEXELS_PROXY || "",
};

export const isStockSourceConfigured = (source: StockSource): boolean => Boolean(PROXY[source]);

// The credit a stock pick owes — parsed and rendered the same way by this app and the public event website, so it
// lives in @em/utils (ROADMAP A2e #95). Re-exported here for the picker's existing importers.
export { STOCK_SERVICES, stockServiceInfo, parseMediaAttribution, mediaCreditParts } from "@em/utils";
export type { MediaAttribution, MediaCreditParts } from "@em/utils";

// #95 — the shape filter (pure, in its own file so a guard can run it without this module's VITE env).
export { STOCK_SHAPES, orientationParam, shapeForCrop, stockShapeSupported } from "./stockShape";
export type { StockShape } from "./stockShape";
export interface StockMediaItem {
  id: string;
  source: StockSource;
  kind: "image" | "video";
  /** Grid thumbnail. */
  thumbUrl: string;
  /** The URL stored on select. */
  fullUrl: string;
  width?: number | null;
  height?: number | null;
  authorName?: string | null;
  authorUrl?: string | null;
  /** Alt / title. */
  description?: string | null;
  /** Provider hooks fired on select (attribution / analytics). Not stored. */
  downloadTrigger?: string | null; // Unsplash download_location
  analyticsUrl?: string | null; // GIPHY analytics onclick
}

export interface StockMediaPage {
  items: StockMediaItem[];
  hasMore: boolean;
  page: number;
  /** True when the proxy rejected the call (e.g. origin not allow-listed). */
  error?: boolean;
}

const EMPTY = (page: number, error = false): StockMediaPage => ({ items: [], hasMore: false, page, error });

// Attribution is REQUIRED by Unsplash/GIPHY/Pexels (show the author + a link to
// them + the service). We only store a URL for stock picks, so we bake the
// credit into the URL as query params — the event website reads `stock`,
// `author`, `authorUrl` back off the URL to render the credit line.
function appendAttribution(item: StockMediaItem): string {
  const url = item.fullUrl;
  if (!url) {
    return url;
  }
  const parts = [`stock=${item.source}`];
  if (item.authorName) {
    parts.push(`author=${encodeURIComponent(item.authorName)}`);
  }
  if (item.authorUrl) {
    parts.push(`authorUrl=${encodeURIComponent(item.authorUrl)}`);
  }
  return url + (url.includes("?") ? "&" : "?") + parts.join("&");
}

// A stock-photo search that never answers ends like any read (ROADMAP #131) — as a failed search (null), not a spinner
// for good.
const STOCK_SEARCH_LIMIT_MS = 30_000;

async function getJson(url: string): Promise<any | null> {
  try {
    const res = await fetchWithLimit(url, {}, { limitMs: STOCK_SEARCH_LIMIT_MS, isWrite: false });
    if (!res.ok) {
      return null;
    }
    return await res.json();
  } catch {
    return null;
  }
}

// The proxy wraps the provider response in { Code, Data, Errors } (Code 0 = OK).
// Unwrap to the provider payload; return `{ ok:false }` when the proxy errored,
// and pass through if a future worker returns the raw provider JSON instead.
function unwrap(env: any): { ok: boolean; payload: any } {
  if (env && typeof env === "object" && "Code" in env) {
    if (env.Code !== 0) {
      return { ok: false, payload: null };
    }
    return { ok: true, payload: env.Data };
  }
  return { ok: env != null, payload: env };
}

// ── Unsplash ──────────────────────────────────────────────────────────────────

const PER_PAGE = 30;

async function searchUnsplash(query: string, page: number, shape: StockShape): Promise<StockMediaPage> {
  const url = `${PROXY.unsplash}/search/photos?query=${encodeURIComponent(query)}&page=${page}&per_page=${PER_PAGE}${orientationParam("unsplash", shape)}`;
  const { ok, payload } = unwrap(await getJson(url));
  if (!ok) {
    return EMPTY(page, true);
  }

  // payload may be [{results,total_pages}], {results,…}, or [...photos].
  const obj = Array.isArray(payload)
    ? payload[0]?.results !== undefined
      ? payload[0]
      : { results: payload, total_pages: undefined }
    : payload ?? {};
  const results: any[] = Array.isArray(obj?.results) ? obj.results : [];

  const items: StockMediaItem[] = results.map((r) => ({
    id: String(r.id),
    source: "unsplash",
    kind: "image",
    thumbUrl: r.urls?.small || r.urls?.thumb || r.urls?.regular || "",
    fullUrl: r.urls?.regular || r.urls?.full || r.urls?.small || "",
    width: r.width ?? null,
    height: r.height ?? null,
    authorName: r.user?.name ?? null,
    authorUrl: r.user?.links?.html ?? null,
    description: r.alt_description ?? r.description ?? null,
    downloadTrigger: r.links?.download_location ?? null,
  }));

  const totalPages = Number(obj?.total_pages ?? 0);
  return { items, hasMore: totalPages ? page < totalPages : results.length === PER_PAGE, page };
}

// ── GIPHY ─────────────────────────────────────────────────────────────────────

const GIPHY_LIMIT = 24;

async function searchGiphy(query: string, page: number): Promise<StockMediaPage> {
  const offset = (page - 1) * GIPHY_LIMIT;
  const url = `${PROXY.giphy}/v1/gifs/search?q=${encodeURIComponent(query)}&limit=${GIPHY_LIMIT}&offset=${offset}`;
  const { ok, payload } = unwrap(await getJson(url));
  if (!ok) {
    return EMPTY(page, true);
  }

  // payload may be [...gifs], [{data,pagination}], or {data,pagination}.
  let results: any[] = [];
  let pag: any = {};
  if (Array.isArray(payload)) {
    if (payload[0]?.data !== undefined) {
      results = payload[0].data;
      pag = payload[0].pagination ?? {};
    } else {
      results = payload.filter((x) => x?.images);
    }
  } else if (payload?.data) {
    results = payload.data;
    pag = payload.pagination ?? {};
  }

  const items: StockMediaItem[] = results.map((g) => ({
    id: String(g.id),
    source: "giphy",
    kind: "image",
    thumbUrl: g.images?.fixed_width?.url || g.images?.fixed_width_downsampled?.url || g.images?.downsized?.url || "",
    fullUrl: g.images?.downsized_medium?.url || g.images?.original?.url || g.images?.downsized?.url || "",
    width: Number(g.images?.original?.width) || null,
    height: Number(g.images?.original?.height) || null,
    authorName: g.user?.display_name || g.username || null,
    authorUrl: g.user?.profile_url || null,
    description: g.title ?? null,
    analyticsUrl: g.analytics?.onclick?.url ?? null,
  }));

  const seen = Number(pag.offset ?? offset) + Number(pag.count ?? results.length);
  const total = Number(pag.total_count ?? 0);
  return { items, hasMore: total > 0 ? seen < total : results.length === GIPHY_LIMIT, page };
}

// ── Pexels (photos + videos) ────────────────────────────────────────────────

async function searchPexels(query: string, page: number, kind: "image" | "video", shape: StockShape): Promise<StockMediaPage> {
  const endpoint = kind === "video" ? "/videos/search" : "/v1/search";
  const url = `${PROXY.pexels}${endpoint}?query=${encodeURIComponent(query)}&per_page=${PER_PAGE}&page=${page}${orientationParam("pexels", shape)}`;
  const unwrapped = unwrap(await getJson(url));
  if (!unwrapped.ok) {
    return EMPTY(page, true);
  }
  const data = Array.isArray(unwrapped.payload) ? unwrapped.payload[0] ?? {} : unwrapped.payload ?? {};

  if (kind === "video") {
    const results: any[] = Array.isArray(data?.videos) ? data.videos : [];
    const items: StockMediaItem[] = results.map((v) => {
      const files: any[] = Array.isArray(v.video_files) ? v.video_files : [];
      // Prefer a mid-size mp4 for the cover.
      const pick =
        files.find((f) => f.quality === "sd" && /mp4/i.test(f.file_type || "")) ||
        files.find((f) => /mp4/i.test(f.file_type || "")) ||
        files[0];
      return {
        id: String(v.id),
        source: "pexels",
        kind: "video",
        thumbUrl: v.image || "",
        fullUrl: pick?.link || "",
        width: v.width ?? null,
        height: v.height ?? null,
        authorName: v.user?.name ?? null,
        authorUrl: v.user?.url ?? null,
        description: null,
      };
    });
    const total = Number(data?.total_results ?? 0);
    return { items, hasMore: total > page * PER_PAGE, page };
  }

  const results: any[] = Array.isArray(data?.photos) ? data.photos : [];
  const items: StockMediaItem[] = results.map((p) => ({
    id: String(p.id),
    source: "pexels",
    kind: "image",
    thumbUrl: p.src?.medium || p.src?.small || p.src?.tiny || "",
    fullUrl: p.src?.large2x || p.src?.large || p.src?.original || "",
    width: p.width ?? null,
    height: p.height ?? null,
    authorName: p.photographer ?? null,
    authorUrl: p.photographer_url ?? null,
    description: p.alt ?? null,
  }));
  const total = Number(data?.total_results ?? 0);
  return { items, hasMore: total > page * PER_PAGE, page };
}

// ── Public API ────────────────────────────────────────────────────────────────

export async function searchStockMedia(
  source: StockSource,
  query: string,
  page: number,
  kind: "image" | "video",
  /** REQUIRED (#95) — "any" for no filter; ignored by a service without one. */
  shape: StockShape,
): Promise<StockMediaPage> {
  if (!isStockSourceConfigured(source) || !query.trim()) {
    return EMPTY(page);
  }
  const result =
    source === "unsplash"
      ? await searchUnsplash(query, page, shape)
      : source === "giphy"
        ? await searchGiphy(query, page)
        : await searchPexels(query, page, kind, shape);

  // Bake author attribution into every stored URL.
  return { ...result, items: result.items.map((it) => ({ ...it, fullUrl: appendAttribution(it) })) };
}

/**
 * Fire the provider's on-select hooks (Unsplash requires triggering the download
 * endpoint; GIPHY an analytics ping). Fire-and-forget — never blocks selection.
 */
export function trackStockSelect(item: StockMediaItem): void {
  try {
    if (item.source === "unsplash" && item.downloadTrigger) {
      const u = new URL(item.downloadTrigger);
      // Proxy the download_location through the same worker (keeps the key server-side).
      void fetch(`${PROXY.unsplash}${u.pathname}${u.search}`).catch(() => undefined);
    } else if (item.source === "giphy" && item.analyticsUrl) {
      void fetch(item.analyticsUrl).catch(() => undefined);
    }
  } catch {
    /* attribution ping is best-effort */
  }
}
